import { useEffect, useMemo, useState } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material/styles';

import { useFeatureFlags, useLanguage } from '../../../hooks';
import { SkipLogicQuestion } from '../../../types/formBuilder';
import { datascopeTracker } from '../../../utils/eventTracker';
import { convertHoursToDaysAndHours } from '../../NewWebForms/Questions/dateQuestion/dateValidationHelpers';
import { InfoAlert } from '../../uiComponents/infoAlert';
import { isUpperBoundCondition, takesNoOffset, takesTwoOffsets } from './comparisons';
import { getOffsetBounds } from './constants';
import { CustomRangeConditionFields } from './customRangeConditionFields';
import {
  DateQuestionField,
  DateQuestionModeType,
  DateQuestionReferenceField,
  DateValidationType,
  RangeConditionType,
} from './types';
import { hasDateValidationErrors } from './utils';

const customStyles: Record<string, SxProps<Theme>> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    marginBottom: '8px',
  },
  select: {
    '& .MuiSelect-select': {
      fontSize: '14px',
    },
  },
  placeholderText: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    verticalAlign: 'middle',
    color: (theme) => theme.palette.grey[600],
  },
  rangeConditionGroupLabel: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '-0.02em',
    color: (theme) => theme.palette.grey[600],
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  validationTypeOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  validationTypeTitle: {
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    color: (theme) => theme.palette.secondary.main,
  },
  validationTypeDescription: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    color: (theme) => theme.palette.grey[600],
    marginTop: '4px',
  },
  divider: {
    margin: '0px',
  },
  timeModeWarning: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '-0.02em',
    color: '#6E7178',
    marginTop: '-6px',
    marginBottom: '1px',
  },
  errorMessage: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '12px',
    lineHeight: '20px',
    marginTop: '4px',
    color: (theme) => theme.palette.error.main,
  },
};

interface DateValidationProps {
  questionId: string;
  /**
   * The questions this one may point at. Scoped by the caller, which is what
   * keeps a relative reference from crossing a repeatable's boundary: a
   * top-level question is handed the top-level list, a question inside a
   * table is handed that table's list, and neither can see the other.
   */
  logicQuestions?: SkipLogicQuestion[];
  referenceQuestionId?: string;
  referenceQuestionIdToLeft?: string;
  referenceQuestionIdToRight?: string;
  dateMode: DateQuestionModeType;
  dateValidationType?: DateValidationType | '';
  rangeCondition?: RangeConditionType;
  offsetHours?: string;
  offsetHoursToLeft?: string;
  offsetHoursToRight?: string;
  onDateValidationChange: (
    questionId: string,
    dateQuestionFieldToUpdate: DateQuestionField,
    value: string,
    tableId?: string,
  ) => void;
  onDateValidationBatchChange?: (
    questionId: string,
    dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
    tableId?: string,
  ) => void;
  onDateValidationTableBatchChange?: (
    questionIdInTable: string,
    dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
    tableId: string,
  ) => void;
  tableId?: string;
}

/**
 * In the relative mode, `between` and `notBetween` take two reference
 * questions and no offset: the seventh comparison, betweenQuestionOffset,
 * exists precisely because those two do not accept one, so the component does
 * not render an offset field for them.
 */
export const DateValidation = (props: DateValidationProps) => {
  const {
    questionId,
    logicQuestions,
    referenceQuestionId,
    referenceQuestionIdToLeft,
    referenceQuestionIdToRight,
    dateMode,
    dateValidationType,
    rangeCondition,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    onDateValidationChange,
    onDateValidationBatchChange,
    onDateValidationTableBatchChange,
    tableId,
  } = props;

  // Hoisted above referenceCandidates: both need the same normalization, and a legacy
  // question's stored dateMode can be absent (DS-4176 review).
  const safeDateMode: DateQuestionModeType = dateMode || 'datetime';

  const referenceCandidates = useMemo(
    () =>
      (logicQuestions ?? []).filter(
        // By subtype, not by type: all three date subtypes share `type === 'date'`
        // and differ only in `dateMode`, so a type filter would offer a `time`
        // question as the reference for a `date` one. Never itself.
        //
        // Both sides are normalized the same way the panel itself is (`safeDateMode`
        // below): a legacy question with no stored `dateMode` renders as `datetime`, so
        // comparing raw `question.dateMode` against raw `dateMode` hid real `datetime`
        // candidates from a legacy panel and vice versa (DS-4176 review).
        (question) =>
          question.type === 'date' &&
          (question.dateMode || 'datetime') === safeDateMode &&
          question.id !== questionId,
      ),
    [logicQuestions, safeDateMode, questionId],
  );

  const [typeOfValidation, setTypeOfValidation] = useState<DateValidationType | '' | undefined>(
    dateValidationType,
  );
  const [selectedRangeCondition, setSelectedRangeCondition] = useState<RangeConditionType>(
    rangeCondition ?? undefined,
  );

  useEffect(() => {
    setTypeOfValidation(dateValidationType);
  }, [dateValidationType]);

  useEffect(() => {
    setSelectedRangeCondition(rangeCondition ?? undefined);
  }, [rangeCondition]);

  // A stored reference that the candidate set no longer contains (the referenced question was
  // deleted, or changed subtype) is otherwise kept and re-saved forever: the Select gets a value
  // matching no MenuItem, the notice silently goes blank, and nothing else reacts to the set
  // shrinking under a stored value (DS-4176 review). Reacts to the candidate set changing, not
  // just mount, so it also catches a candidate disappearing while the panel stays open.
  useEffect(() => {
    const isDangling = (value?: string) =>
      !!value && !referenceCandidates.some((question) => question.id === value);

    if (isDangling(referenceQuestionId)) {
      onDateValidationChange(questionId, 'referenceQuestionId', '', tableId);
    }
    if (isDangling(referenceQuestionIdToLeft)) {
      onDateValidationChange(questionId, 'referenceQuestionIdToLeft', '', tableId);
    }
    if (isDangling(referenceQuestionIdToRight)) {
      onDateValidationChange(questionId, 'referenceQuestionIdToRight', '', tableId);
    }
  }, [
    referenceCandidates,
    referenceQuestionId,
    referenceQuestionIdToLeft,
    referenceQuestionIdToRight,
    questionId,
    tableId,
    onDateValidationChange,
  ]);

  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();
  const notBetweenEnabled = checkFlag('DateRelativeRangeValidation');
  const relativeRangeEnabled = notBetweenEnabled;

  const validationTypes: {
    value: DateValidationType;
    label: string;
    description: string;
    disabled?: boolean;
  }[] = [
    {
      value: 'fixedRange',
      label: lang.form_builder.dateValidation.fixedRange,
      description:
        safeDateMode === 'time'
          ? lang.form_builder.dateValidation.fixedRangeDescriptionTime
          : lang.form_builder.dateValidation.fixedRangeDescription,
    },
    {
      value: 'relativeRange',
      label: lang.form_builder.dateValidation.relativeRange,
      description:
        safeDateMode === 'time'
          ? lang.form_builder.dateValidation.relativeRangeDescriptionTime
          : lang.form_builder.dateValidation.relativeRangeDescription,
      // Same shape as the notBetween gate above: the flag decides what is
      // offered, not what can be displayed. A question saved while the flag
      // was on must stay selectable after it goes off, or the panel shows an
      // empty mode over a configuration that is still stored.
      disabled: !relativeRangeEnabled && dateValidationType !== 'relativeRange',
    },
  ];

  const hasValidation = dateValidationType !== undefined;

  const predefinedConditions =
    safeDateMode === 'time'
      ? [
          { value: 'onlyToday', label: lang.form_builder.dateValidation.onlyCurrentTime },
          { value: 'noFutureDates', label: lang.form_builder.dateValidation.noFutureTimes },
          { value: 'noPastDates', label: lang.form_builder.dateValidation.noPastTimes },
        ]
      : [
          { value: 'onlyToday', label: lang.form_builder.dateValidation.onlyToday },
          { value: 'noFutureDates', label: lang.form_builder.dateValidation.noFutureDates },
          { value: 'noPastDates', label: lang.form_builder.dateValidation.noPastDates },
        ];

  const notBetweenCondition = {
    value: 'notBetween',
    label: lang.form_builder.dateValidation.notBetween,
  };

  const betweenQuestionOffsetCondition = {
    value: 'betweenQuestionOffset',
    label: lang.form_builder.dateValidation.betweenQuestionOffset,
  };

  const baseCustomConditions = [
    { value: 'after', label: lang.form_builder.dateValidation.after },
    { value: 'afterOrEqual', label: lang.form_builder.dateValidation.afterOrEqual },
    { value: 'before', label: lang.form_builder.dateValidation.before },
    { value: 'beforeOrEqual', label: lang.form_builder.dateValidation.beforeOrEqual },
    { value: 'between', label: lang.form_builder.dateValidation.between },
  ];

  // What the dropdown offers is gated; what the panel can put a name to is not. A question saved
  // while the flag was on still has to render its own label after the flag goes off, and the
  // Select would otherwise show an empty value over a config that is still stored.
  const customConditions = notBetweenEnabled
    ? [...baseCustomConditions, notBetweenCondition]
    : baseCustomConditions;

  /**
   * The relative mode's own comparison list.
   *
   * It is not the fixed mode's. The three predefined conditions are anchored to
   * the moment of answering, which is the one thing the relative mode does not
   * do, so they have no meaning here. And it carries a seventh comparison the
   * fixed mode has no use for: a range running from the reference answer to
   * that answer plus the offset.
   */
  const relativeRangeConditions = relativeRangeEnabled
    ? [...baseCustomConditions, notBetweenCondition, betweenQuestionOffsetCondition]
    : baseCustomConditions;

  /**
   * Every comparison the panel may have to put a name to, in either mode.
   * Kept complete on purpose: resolving a saved value's label must not depend
   * on which mode is active or which flag is on.
   */
  const allRangeConditions = [
    ...predefinedConditions,
    ...baseCustomConditions,
    notBetweenCondition,
    betweenQuestionOffsetCondition,
  ];

  const defaultOffsetInHours = getOffsetBounds(safeDateMode, typeOfValidation).min.toString();

  /**
   * One reference selector.
   *
   * Empty candidate list is a real state, not an edge case: a form whose only
   * `date` question is this one has nothing to compare against, and the mode
   * cannot be completed. Saying so beats an empty dropdown that looks broken.
   */
  /**
   * Whether picking `newValue` for `field` completes the rule: the one reference a one-sided
   * comparison needs, or the second of the two-sided pair's references once the other is
   * already set. Order-independent on purpose, since either of the pair may be picked first.
   */
  const completesReferenceRule = (field: DateQuestionReferenceField, newValue: string) => {
    if (!newValue) return false;
    if (field === 'referenceQuestionId') return true;
    if (field === 'referenceQuestionIdToLeft') return !!referenceQuestionIdToRight;
    return !!referenceQuestionIdToLeft;
  };

  const renderReferenceSelect = (
    label: string,
    field: DateQuestionReferenceField,
    value?: string,
  ) => (
    <Box sx={customStyles.fieldContainer}>
      <Typography sx={customStyles.label}>{label}</Typography>
      {referenceCandidates.length === 0 ? (
        <>
          <Select
            value=''
            size='small'
            fullWidth
            disabled
            sx={customStyles.select}
            displayEmpty
            data-testid={`date-validation-reference-${field}`}
          >
            <MenuItem
              value=''
              disabled
            >
              <Typography sx={customStyles.placeholderText}>
                {lang.form_builder.dateValidation.pickQuestion}
              </Typography>
            </MenuItem>
          </Select>
          <Typography sx={customStyles.errorMessage}>
            {lang.form_builder.dateValidation.noQuestionsAvailable}
          </Typography>
        </>
      ) : (
        <Select
          value={value ?? ''}
          size='small'
          fullWidth
          sx={customStyles.select}
          displayEmpty
          data-testid={`date-validation-reference-${field}`}
          onChange={(event) => {
            const newValue = event.target.value as string;
            // Fires once the rule this comparison needs is actually complete, not on every
            // reference pick along the way (DS-4176 review, same reasoning as
            // handleRangeConditionChange above).
            if (completesReferenceRule(field, newValue)) {
              datascopeTracker('date_relative_validation_configured', {
                comparison: selectedRangeCondition,
                dateMode: safeDateMode,
                insideTable: Boolean(tableId),
              });
            }
            onDateValidationChange(questionId, field, newValue, tableId);
          }}
        >
          <MenuItem
            value=''
            disabled
          >
            <Typography sx={customStyles.placeholderText}>
              {lang.form_builder.dateValidation.pickQuestion}
            </Typography>
          </MenuItem>
          {referenceCandidates.map((question) => (
            <MenuItem
              key={question.id}
              value={question.id}
            >
              {question.name}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );

  /**
   * The selectors a comparison needs, and how they are labelled.
   *
   * The label follows the comparison rather than the field: the same reference
   * is the lower bound in `after` and the upper bound in `before`, so naming it
   * after its slot would mislabel half the cases.
   */
  const renderReferenceFields = () => {
    if (takesTwoOffsets(selectedRangeCondition)) {
      return (
        <>
          {renderReferenceSelect(
            lang.form_builder.dateValidation.minimumValue,
            'referenceQuestionIdToLeft',
            referenceQuestionIdToLeft,
          )}
          {renderReferenceSelect(
            lang.form_builder.dateValidation.maximumValue,
            'referenceQuestionIdToRight',
            referenceQuestionIdToRight,
          )}
        </>
      );
    }

    const isUpperBound = isUpperBoundCondition(selectedRangeCondition);
    return renderReferenceSelect(
      isUpperBound
        ? lang.form_builder.dateValidation.maximumValue
        : lang.form_builder.dateValidation.minimumValue,
      'referenceQuestionId',
      referenceQuestionId,
    );
  };

  /**
   * Builds the fields object for a newly-selected range condition: the offset fields the new
   * condition does not use, and (relative mode) the reference field(s) it does not use, so
   * neither kind is left behind from the previous condition's shape.
   *
   * A batch-change field is only written to the question when it appears in this object
   * (`onDateValidationBatchChange`/`onDateValidationTableBatchChange` iterate its own keys), so
   * omitting a still-relevant field here leaves whatever the question already had for it
   * untouched, while every field the new shape does not use is explicitly cleared.
   *
   * Reference fields matter only in relative mode; fixed mode never reads them, so leaving them
   * unset there is harmless, and clearing them anyway would cost an extra condition for no
   * behavior difference.
   */
  const buildDefaultOffsetFields = (
    condition: RangeConditionType,
  ): Partial<Record<DateQuestionField, string>> => {
    const fields: Partial<Record<DateQuestionField, string>> = {
      rangeCondition: condition,
    };

    if (!condition) {
      return fields;
    }

    const isPredefinedRangeCondition = takesNoOffset(condition);
    const isBetweenTypeRangeCondition = takesTwoOffsets(condition);

    if (isPredefinedRangeCondition) {
      fields.offsetHours = '';
      fields.offsetHoursToLeft = '';
      fields.offsetHoursToRight = '';
      // Predefined conditions take no reference of any arity (DS-4176 review: a stale
      // referenceQuestionId* left over from a prior comparison would otherwise still save).
      fields.referenceQuestionId = '';
      fields.referenceQuestionIdToLeft = '';
      fields.referenceQuestionIdToRight = '';
    } else if (isBetweenTypeRangeCondition) {
      fields.offsetHours = '';
      fields.offsetHoursToLeft = defaultOffsetInHours;
      fields.offsetHoursToRight = defaultOffsetInHours;
      // Two-sided takes referenceQuestionIdToLeft/ToRight, not the one-sided field (DS-4176
      // review: switching away from a one-sided comparison left its reference behind as an
      // orphan the two-sided rule has no slot for).
      fields.referenceQuestionId = '';
    } else {
      fields.offsetHours = defaultOffsetInHours;
      fields.offsetHoursToLeft = '';
      fields.offsetHoursToRight = '';
      // Mirror image of the branch above: one-sided takes referenceQuestionId, not the
      // two-sided pair.
      fields.referenceQuestionIdToLeft = '';
      fields.referenceQuestionIdToRight = '';
    }

    return fields;
  };

  const convertHoursToDaysAndHoursWithUnitText = (hours: string) => {
    const { days, hours: remainingHours } = convertHoursToDaysAndHours(hours);

    const daysUnitText =
      days === 1
        ? lang.form_builder.dateValidation.day.toLowerCase()
        : lang.form_builder.dateValidation.days.toLowerCase();
    const hoursUnitText =
      remainingHours === 1
        ? lang.form_builder.dateValidation.hour.toLowerCase()
        : lang.form_builder.dateValidation.hours.toLowerCase();

    return {
      days,
      hours: remainingHours,
      daysUnitText,
      hoursUnitText,
    };
  };

  const formatTimeText = (
    days: number,
    daysUnit: string,
    hours: number,
    hoursUnit: string,
  ): string => {
    if (days > 0 && hours > 0) {
      return `${days} ${daysUnit} ${lang.form_builder.dateValidation.and} ${hours} ${hoursUnit}`;
    } else if (days > 0) {
      return `${days} ${daysUnit}`;
    } else if (hours > 0) {
      return `${hours} ${hoursUnit}`;
    }
    return '';
  };

  // Shared by `between` and `notBetween`, which format the same two-sided offset into
  // whichever message template the caller passes, and differ only in that template.
  const formatRangeMessage = (
    messageTemplate: string,
    left: string | undefined,
    right: string | undefined,
    mode: DateQuestionModeType,
  ): string => {
    if (!left || !right) return '';
    const before = convertHoursToDaysAndHoursWithUnitText(left);
    const after = convertHoursToDaysAndHoursWithUnitText(right);
    if (mode === 'datetime') {
      const timeTextBefore = formatTimeText(
        before.days,
        before.daysUnitText,
        before.hours,
        before.hoursUnitText,
      );
      const timeTextAfter = formatTimeText(
        after.days,
        after.daysUnitText,
        after.hours,
        after.hoursUnitText,
      );
      const replacementPatternBefore = `{daysBefore} {daysBeforeUnit} ${lang.form_builder.dateValidation.and} {hoursBefore} {hoursBeforeUnit}`;
      const replacementPatternAfter = `{daysAfter} {daysAfterUnit} ${lang.form_builder.dateValidation.and} {hoursAfter} {hoursAfterUnit}`;
      return messageTemplate
        .replace(replacementPatternBefore, timeTextBefore)
        .replace(replacementPatternAfter, timeTextAfter);
    }
    return messageTemplate
      .replace('{daysBefore}', before.days.toString())
      .replace('{daysBeforeUnit}', before.daysUnitText)
      .replace('{hoursBefore}', before.hours.toString())
      .replace('{hoursBeforeUnit}', before.hoursUnitText)
      .replace('{daysAfter}', after.days.toString())
      .replace('{daysAfterUnit}', after.daysUnitText)
      .replace('{hoursAfter}', after.hours.toString())
      .replace('{hoursAfterUnit}', after.hoursUnitText);
  };

  const referenceNameOf = (id?: string) =>
    referenceCandidates.find((question) => question.id === id)?.name ?? '';

  /**
   * The panel's rule, in words, for the relative mode.
   *
   * It names the reference **question**, not a computed date. That is the
   * opposite of the answer-time messages, and deliberately: here the
   * administrator is choosing a rule and needs to recognise what they picked,
   * while the person answering needs the actual boundary they have to clear.
   * Same feature, two audiences, two texts.
   *
   * Empty while the configuration is incomplete. A half-written rule stated as
   * if it were whole is worse than no sentence at all.
   */
  const relativeValidationMessage = (() => {
    if (typeOfValidation !== 'relativeRange' || !selectedRangeCondition) return '';

    const messages = lang.form_builder.dateValidation[safeDateMode] as Record<string, string>;
    const { days, hours, daysUnitText, hoursUnitText } = convertHoursToDaysAndHoursWithUnitText(
      offsetHours || '0',
    );

    // The datetime templates below spell the days-and-hours group as one literal chunk
    // ("{days} {daysUnit} y {hours} {hoursUnit}"). Substituting {days}/{hours} independently
    // (as `fill` below does) never drops a zero component, so relative datetime — capped at 23
    // hours, so `days` is always 0 — always read "mas 0 dias y 2 horas". Replacing the whole
    // chunk with formatTimeText's output collapses it, the same trick `validationMessage`
    // already uses for the fixed-mode equivalent (DS-4176 review).
    const applyDaysAndHours = (template?: string) => {
      if (!template || safeDateMode !== 'datetime') return template;
      const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
      const pattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
      return template.replace(pattern, timeText);
    };

    const fill = (template?: string, names?: Record<string, string>) =>
      template
        ? Object.entries({
            days: String(days),
            daysUnit: daysUnitText,
            hours: String(hours),
            hoursUnit: hoursUnitText,
            ...(names ?? {}),
          }).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)
        : '';

    if (takesTwoOffsets(selectedRangeCondition)) {
      const minName = referenceNameOf(referenceQuestionIdToLeft);
      const maxName = referenceNameOf(referenceQuestionIdToRight);
      if (!minName || !maxName) return '';
      const key =
        selectedRangeCondition === 'between'
          ? 'relativeBetweenMessage'
          : 'relativeNotBetweenMessage';
      // No days/hours group in these templates (a two-sided reference, no offset) —
      // applyDaysAndHours is only needed for the one-sided branch below.
      return fill(messages[key], { minQuestionName: minName, maxQuestionName: maxName });
    }

    const questionName = referenceNameOf(referenceQuestionId);
    if (!questionName) return '';

    const keyByCondition: Record<string, string> = {
      after: 'relativeAfterMessage',
      afterOrEqual: 'relativeAfterOrEqualMessage',
      before: 'relativeBeforeMessage',
      beforeOrEqual: 'relativeBeforeOrEqualMessage',
      betweenQuestionOffset: 'relativeBetweenQuestionOffsetMessage',
    };
    return fill(applyDaysAndHours(messages[keyByCondition[selectedRangeCondition] ?? '']), {
      questionName,
    });
  })();

  const validationMessage = (() => {
    if (!selectedRangeCondition || typeOfValidation === 'relativeRange') return '';

    const validationMessages = lang.form_builder.dateValidation[safeDateMode];

    switch (selectedRangeCondition) {
      case 'after': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHoursWithUnitText(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.afterMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.afterMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'afterOrEqual': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHoursWithUnitText(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.afterOrEqualMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.afterOrEqualMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'before': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHoursWithUnitText(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.beforeMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.beforeMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'beforeOrEqual': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHoursWithUnitText(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.beforeOrEqualMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.beforeOrEqualMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'between':
        return formatRangeMessage(
          validationMessages.betweenMessage,
          offsetHoursToLeft,
          offsetHoursToRight,
          safeDateMode,
        );

      case 'notBetween':
        return formatRangeMessage(
          validationMessages.notBetweenMessage,
          offsetHoursToLeft,
          offsetHoursToRight,
          safeDateMode,
        );

      default:
        return '';
    }
  })();

  const hasValidationErrors = hasDateValidationErrors({
    selectedRangeCondition,
    dateMode: safeDateMode,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    dateValidationType: typeOfValidation,
  });

  // Empty until every reference the rule needs has actually been picked (see
  // relativeValidationMessage above) — the banner below must stay hidden for exactly as long,
  // rather than showing an icon over a blank sentence while the rule is still incomplete.
  const displayedValidationMessage =
    typeOfValidation === 'relativeRange' ? relativeValidationMessage : validationMessage;

  const handleTypeOfValidationChange = (value: DateValidationType) => {
    const oldTypeOfValidation = typeOfValidation;
    setTypeOfValidation(value);

    if (!value) return;

    if (oldTypeOfValidation !== value) {
      setSelectedRangeCondition(undefined);

      // The reference fields belong here too. Without them, switching relative
      // to fixed leaves them behind and they serialize alongside
      // dateValidationType: 'fixedRange' — a question carrying configuration
      // for a mode it is no longer in.
      const fieldsToClear: Partial<Record<DateQuestionField, string>> = {
        rangeCondition: '',
        offsetHours: '',
        offsetHoursToLeft: '',
        offsetHoursToRight: '',
        referenceQuestionId: '',
        referenceQuestionIdToLeft: '',
        referenceQuestionIdToRight: '',
      };

      if (tableId) {
        onDateValidationTableBatchChange?.(questionId, fieldsToClear, tableId);
      } else {
        onDateValidationBatchChange?.(questionId, fieldsToClear);
      }
    }

    onDateValidationChange(questionId, 'dateValidationType', value, tableId);
  };

  const handleRangeConditionChange = (condition: RangeConditionType) => {
    setSelectedRangeCondition(condition);
    if (!condition) return;

    // Tracking moved to renderReferenceSelect's onChange: picking the comparison alone does not
    // configure a usable rule yet (no reference is chosen here, and a predefined condition takes
    // none at all), so firing here counted dropdown browsing rather than adoption of the mode —
    // several events per configured question, and none for the step that actually completes one
    // (DS-4176 review).

    const dateQuestionFieldsToUpdate = buildDefaultOffsetFields(condition);

    if (tableId) {
      onDateValidationTableBatchChange?.(questionId, dateQuestionFieldsToUpdate, tableId);
    } else {
      onDateValidationBatchChange?.(questionId, dateQuestionFieldsToUpdate);
    }
  };

  const renderValidationTypeSelectedValue = (selectedValidationType: DateValidationType | '') => {
    if (!selectedValidationType) {
      return (
        <Typography sx={customStyles.placeholderText}>
          {lang.form_builder.dateValidation.selectRangeCondition}
        </Typography>
      );
    }
    const selectedType = validationTypes.find((type) => type.value === selectedValidationType);
    return selectedType?.label ?? '';
  };

  const renderRangeConditionSelectedValue = (
    selectedRangeConditionType: RangeConditionType | '',
  ) => {
    if (!selectedRangeConditionType) {
      return (
        <Typography sx={customStyles.placeholderText}>
          {lang.form_builder.dateValidation.selectRangeCondition}
        </Typography>
      );
    }
    const selectedOption = allRangeConditions.find(
      (option) => option.value === selectedRangeConditionType,
    );
    return selectedOption?.label ?? '';
  };

  /**
   * The fixed mode's two labelled groups of range-condition MenuItems.
   *
   * A flat array with explicit keys, not a `<>...</>` fragment: Select clones its children with
   * `React.Children.map` to inject selection handling, and that map does not descend into a
   * Fragment, so a fragment here would render the menu items' text but leave them unclickable.
   * Extracted out of the render/return block per AGENTS.md's no-inline-JSX-comments rule
   * (DS-4176 review).
   */
  const renderFixedRangeMenuItems = () => [
    <Typography
      key='predefined-label'
      sx={customStyles.rangeConditionGroupLabel}
    >
      {lang.form_builder.dateValidation.predefinedConditions}
    </Typography>,
    ...predefinedConditions.map((option) => (
      <MenuItem
        key={option.value}
        value={option.value}
      >
        {option.label}
      </MenuItem>
    )),
    <Typography
      key='custom-label'
      sx={customStyles.rangeConditionGroupLabel}
    >
      {lang.form_builder.dateValidation.customConditions}
    </Typography>,
    ...customConditions.map((option) => (
      <MenuItem
        key={option.value}
        value={option.value}
      >
        {option.label}
      </MenuItem>
    )),
  ];

  if (!hasValidation) {
    return null;
  }

  return (
    <Box sx={customStyles.container}>
      <Box sx={customStyles.fieldContainer}>
        <Typography sx={customStyles.label}>
          {lang.form_builder.dateValidation.validationType}
        </Typography>
        <Select
          value={typeOfValidation ?? ''}
          size='small'
          fullWidth
          sx={customStyles.select}
          onChange={(event) =>
            handleTypeOfValidationChange(event.target.value as DateValidationType)
          }
          displayEmpty
          renderValue={renderValidationTypeSelectedValue}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {validationTypes.map((type) => (
            <MenuItem
              key={type.value}
              value={type.value}
              disabled={type.disabled}
            >
              <Box sx={customStyles.validationTypeOption}>
                <Typography sx={customStyles.validationTypeTitle}>{type.label}</Typography>
                <Typography sx={customStyles.validationTypeDescription}>
                  {type.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Box>

      <>
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>
            {lang.form_builder.dateValidation.rangeCondition}
          </Typography>
          <Select
            value={selectedRangeCondition ?? ''}
            size='small'
            fullWidth
            sx={customStyles.select}
            onChange={(event) =>
              handleRangeConditionChange(event.target.value as RangeConditionType)
            }
            displayEmpty
            renderValue={renderRangeConditionSelectedValue}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            {typeOfValidation === 'relativeRange'
              ? relativeRangeConditions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))
              : renderFixedRangeMenuItems()}
          </Select>
        </Box>

        {typeOfValidation === 'relativeRange' &&
          selectedRangeCondition &&
          !takesNoOffset(selectedRangeCondition) &&
          renderReferenceFields()}

        {selectedRangeCondition &&
          !(typeOfValidation === 'relativeRange' && takesTwoOffsets(selectedRangeCondition)) && (
            <Box sx={customStyles.fieldContainer}>
              <CustomRangeConditionFields
                selectedRangeCondition={selectedRangeCondition}
                dateMode={dateMode}
                offsetHours={offsetHours}
                offsetHoursToLeft={offsetHoursToLeft}
                offsetHoursToRight={offsetHoursToRight}
                questionId={questionId}
                tableId={tableId}
                onDateValidationChange={onDateValidationChange}
                dateValidationType={typeOfValidation}
              />
            </Box>
          )}

        {dateMode === 'time' && selectedRangeCondition && !hasValidationErrors && (
          <Typography sx={customStyles.timeModeWarning}>
            {typeOfValidation === 'relativeRange'
              ? lang.form_builder.dateValidation.relativeTimeModeWarning
              : lang.form_builder.dateValidation.timeModeWarning}
          </Typography>
        )}
        {selectedRangeCondition &&
          !takesNoOffset(selectedRangeCondition) &&
          !hasValidationErrors &&
          displayedValidationMessage && (
            <InfoAlert
              testId='date-validation-rule-notice'
              message={displayedValidationMessage}
            />
          )}
      </>
      <Divider sx={customStyles.divider} />
    </Box>
  );
};
